import { OfficerRole } from '../models/officer.model';
import { WorkflowAction } from '../services/ops.service';

/**
 * UI modules ↔ roles (desk access matrix) — aligned to Jansunwai flow.
 *
 * Runtime nav/guards use DB permission codes via MODULE_ACCESS_PERMISSION
 * (see RbacService.canModule). MODULE_ROLES remains as documentation of the
 * historical seed intent / fallback reference only.
 */
export type AppModule =
  | 'dashboard'
  | 'complaints'
  | 'workflow'
  | 'cases'
  | 'scheduling'
  | 'roster'
  | 'hearingManagement'
  | 'resolution'
  | 'notifications'
  | 'reports'
  | 'documentOcr'
  | 'assistant'
  | 'settings'
  | 'roles'
  | 'profile';

/** Fixed platform roles — everything else is a custom investigative desk. */
export const PLATFORM_ROLES = ['admin', 'dm', 'officer', 'peshkar', 'records_officer', 'citizen'] as const;

/**
 * AppModule → catalog ACCESS permission_code (sql/011_rbac_permission_catalog.sql).
 * Nav and roleGuard require this code (Super Admin bypasses in RbacService).
 */
export const MODULE_ACCESS_PERMISSION: Record<AppModule, string> = {
  dashboard: 'DASHBOARD_ACCESS',
  complaints: 'COMPLAINTS_ACCESS',
  workflow: 'WORKFLOW_ACCESS',
  cases: 'CASES_ACCESS',
  scheduling: 'SCHEDULING_ACCESS',
  roster: 'ROSTER_ACCESS',
  hearingManagement: 'HEARING_MANAGEMENT_ACCESS',
  resolution: 'RESOLUTION_ACCESS',
  notifications: 'NOTIFICATIONS_ACCESS',
  reports: 'REPORTS_ACCESS',
  documentOcr: 'DOCUMENT_OCR_ACCESS',
  assistant: 'ASSISTANT_ACCESS',
  settings: 'SETTINGS_ACCESS',
  roles: 'ROLES_ACCESS',
  profile: 'PROFILE_ACCESS',
};

/**
 * Investigator desks: built-in `officer` + any Admin-created desk role
 * (e.g. tehsildar, naib_tehsildar). They see assigned complaints and submit reports.
 * `records_officer` is excluded — it owns Document/OCR intake only, not grievance
 * investigation, so it must not inherit `officer`'s complaint/case permissions.
 */
export function isInvestigatorDesk(role: string | null | undefined): boolean {
  if (!role) return false;
  if (role === 'officer') return true;
  return !['admin', 'dm', 'peshkar', 'citizen', 'records_officer'].includes(role);
}

/** Complaint/case lists scoped to `assignedOfficerRef === me`. */
export function isAssigneeScoped(role: string | null | undefined): boolean {
  return isInvestigatorDesk(role);
}

/**
 * @deprecated Prefer MODULE_ACCESS_PERMISSION + session.permissionCodes.
 * Kept as the documented seed-era role matrix for tests/reference.
 */
export const MODULE_ROLES: Record<AppModule, OfficerRole[]> = {
  dashboard: ['admin', 'dm', 'officer', 'peshkar', 'records_officer'],
  complaints: ['admin', 'dm', 'officer'],
  workflow: [],
  cases: ['officer', 'peshkar'],
  scheduling: ['peshkar'],
  roster: ['peshkar'],
  hearingManagement: [],
  resolution: [],
  notifications: ['admin', 'dm', 'officer', 'peshkar'],
  reports: [],
  documentOcr: ['admin', 'records_officer'],
  assistant: ['admin', 'dm'],
  settings: ['admin'],
  roles: ['admin'],
  profile: ['admin', 'dm', 'officer', 'peshkar', 'records_officer'],
};

export const WORKFLOW_ACTION_ROLES: Record<WorkflowAction, OfficerRole[]> = {
  Approve: ['admin', 'dm'],
  Correct: ['admin', 'dm'],
  Resend: ['admin', 'dm'],
  'Assign Department': ['admin', 'dm'],
  'Assign Officer': ['admin', 'dm'],
  Escalate: ['admin', 'dm'],
  'Update Status': ['admin', 'dm'],
  Close: ['admin', 'dm'],
  Reopen: ['admin', 'dm'],
  'Submit Report': ['admin', 'officer'],
  'Need Hearing': ['admin', 'dm'],
  'Mark Heard': ['admin', 'dm'],
  Resolve: ['admin', 'dm'],
  Reject: ['admin', 'dm'],
};

/**
 * Exact role match, or custom investigator desk matching any allow-list that includes `officer`.
 */
export function roleAllowed(
  role: string | OfficerRole | undefined | null,
  allowed: readonly string[],
): boolean {
  if (!role) return false;
  if (allowed.includes(role)) return true;
  if (isInvestigatorDesk(role) && allowed.includes('officer')) return true;
  return false;
}

/** True when session grants the ACCESS code for a module (admin always true). */
export function canAccessModule(
  role: string | null | undefined,
  permissionCodes: readonly string[] | null | undefined,
  module: AppModule,
  fullAccess?: boolean,
): boolean {
  if (role === 'admin' || fullAccess === true) return true;
  const need = MODULE_ACCESS_PERMISSION[module];
  if (!need) return false;
  if (!permissionCodes?.length) return false;
  return permissionCodes.includes(need);
}
